import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import ConversationFunnelsAPI from '../../api/conversationFunnels';

export const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getConversationFunnels: _state => conversationId => {
    return _state.records[conversationId] || [];
  },
};

export const actions = {
  get: async ({ commit }, conversationId) => {
    commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isFetching: true });
    try {
      const response = await ConversationFunnelsAPI.getFunnels(conversationId);
      commit(types.SET_CONVERSATION_FUNNELS, {
        conversationId,
        data: response.data.conversation_funnels,
      });
    } catch (error) {
      console.error('Error fetching funnels:', error);
    } finally {
      commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit, dispatch, rootState }, { conversationId, kanbanColumnId }) => {
    commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isCreating: true });
    try {
      const response = await ConversationFunnelsAPI.addToFunnel(conversationId, kanbanColumnId);
      
      // Adicionar o kanban_card_id ao funil se disponível
      const funnelData = {
        ...response.data,
        kanban_card_id: response.data.kanban_card_id
      };
      
      // PRIMEIRO: Adicionar o funil ao store imediatamente para exibição
      commit(types.ADD_CONVERSATION_FUNNEL, {
        conversationId,
        data: funnelData,
      });
      
      // SEGUNDO: Atualizar a conversa com os novos kanban_cards se disponível
      if (response.data.conversation?.kanban_cards) {
        const conversation = rootState.conversations.allConversations.find(c => c.id === conversationId);
        if (conversation) {
          // Atualizar a conversa com os novos cards IMEDIATAMENTE
          const updatedConversation = {
            ...conversation,
            kanban_cards: response.data.conversation.kanban_cards
          };
          commit('conversations/UPDATE_CONVERSATION', updatedConversation, { root: true });
        }
      }
      
      // TERCEIRO: Disparar evento global para notificar outros componentes
      if (typeof window !== 'undefined') {
        const kanbanCardId = response.data.kanban_card_id;
        const newCardFromResponse = response.data.conversation?.kanban_cards?.find(card => 
          card.kanban_column_id === kanbanColumnId
        );
        const finalCardId = kanbanCardId || newCardFromResponse?.id;
        
        window.dispatchEvent(new CustomEvent('funnelUpdated', {
          detail: { 
            conversationId, 
            action: 'create', 
            currentCardId: finalCardId,
            kanbanColumnId: kanbanColumnId,
            newCards: response.data.conversation?.kanban_cards
          }
        }));
      }
      
      // QUARTO: Recarregamentos em background (não bloquear a UI)
      setTimeout(async () => {
        try {
          // Limpar cache de produtos se necessário
          const conversation = rootState.conversations.allConversations.find(c => c.id === conversationId);
          if (conversation?.kanban_cards) {
            conversation.kanban_cards.forEach(card => {
              commit('kanbanCardProducts/CLEAR_KANBAN_CARD_PRODUCTS', card.id, { root: true });
            });
          }
          
          // Recarregamentos para sincronização
          await Promise.all([
            dispatch('getConversation', conversationId, { root: true }),
            dispatch('get', conversationId)
          ]);
        } catch (error) {
          console.error('Erro no recarregamento em background:', error);
        }
      }, 100);
      
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit, dispatch, state }, { conversationId, funnelId, kanbanColumnId }) => {
    commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isUpdating: true });
    try {
      // Encontrar o funil atual para obter a coluna de origem
      const currentFunnel = state.records[conversationId]?.find(f => f.id === funnelId);
      const fromColumnId = currentFunnel?.kanban_column_id;
      
      const response = await ConversationFunnelsAPI.moveInFunnel(conversationId, funnelId, kanbanColumnId);
      
      commit(types.EDIT_CONVERSATION_FUNNEL, {
        conversationId,
        data: response.data,
      });
      
      // Disparar evento para sincronizar com Kanban
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('funnelUpdated', {
          detail: { 
            conversationId, 
            action: 'move', 
            funnelId,
            fromColumnId,
            toColumnId: kanbanColumnId
          }
        }));
      }
      
      // Recarregar os dados para manter sincronizado
      await dispatch('get', conversationId);
      return response.data;
    } catch (error) {
      console.error('❌ Erro no update:', error);
      throw error;
    } finally {
      commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit, dispatch, rootState }, { conversationId, funnelId }) => {
    commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isDeleting: true });
    try {
      // Buscar o card que será deletado para limpar cache de produtos
      const conversation = rootState.conversations.allConversations.find(c => c.id === conversationId);
      const cardToDelete = conversation?.kanban_cards?.find(card => 
        card.kanban_column_id === funnelId
      );
      
      await ConversationFunnelsAPI.removeFromFunnel(conversationId, funnelId);
      commit(types.DELETE_CONVERSATION_FUNNEL, {
        conversationId,
        funnelId,
      });
      
      // Limpar cache de produtos do card deletado
      if (cardToDelete?.id) {
        commit('kanbanCardProducts/CLEAR_CARD_PRODUCTS', cardToDelete.id, { root: true });
      }
      
      // Recarregar os dados para manter sincronizado
      await dispatch('get', conversationId);
      
      // Disparar evento global para notificar outros componentes
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('funnelUpdated', {
          detail: { conversationId, action: 'delete', deletedCardId: cardToDelete?.id }
        }));
      }
      
      // Também forçar recarregamento de todas as conversas para garantir sincronização
      dispatch('conversations/fetchAllConversations', null, { root: true });
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CONVERSATION_FUNNELS_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_CONVERSATION_FUNNELS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CONVERSATION_FUNNELS](_state, { conversationId, data }) {
    const existingFunnels = _state.records[conversationId] || [];
    const newData = data || [];
    
    // Se já há funis existentes, preservar a ordem mantendo os IDs na mesma posição
    if (existingFunnels.length > 0) {
      // Criar um mapa dos novos dados por ID
      const newDataMap = new Map(newData.map(funnel => [funnel.id, funnel]));
      
      // Atualizar funis existentes mantendo a ordem, removendo os que não existem mais
      const updated = existingFunnels
        .map(existing => {
          const updated = newDataMap.get(existing.id);
          if (updated) {
            // Remover do mapa para não adicionar duplicado depois
            newDataMap.delete(existing.id);
            return {
              ...updated,
              kanban_card_id: updated.kanban_card_id || existing.kanban_card_id
            };
          }
          // Retornar null para funis que foram deletados
          return null;
        })
        .filter(Boolean); // Remover nulls (funis deletados)
      
      // Adicionar novos funis que não existiam antes (ao final)
      const newFunnels = Array.from(newDataMap.values()).map(funnel => ({
        ...funnel,
        kanban_card_id: funnel.kanban_card_id
      }));
      
      _state.records[conversationId] = [...updated, ...newFunnels];
    } else {
      // Se não há funis existentes, apenas processar os novos dados
      const processedData = newData.map(funnel => ({
        ...funnel,
        kanban_card_id: funnel.kanban_card_id
      }));
      _state.records[conversationId] = processedData;
    }
  },

  [types.ADD_CONVERSATION_FUNNEL](_state, { conversationId, data }) {
    if (!_state.records[conversationId]) {
      _state.records[conversationId] = [];
    }
    // Garantir que o kanban_card_id seja preservado
    const funnelData = {
      ...data,
      kanban_card_id: data.kanban_card_id
    };
    _state.records[conversationId].push(funnelData);
  },

  [types.EDIT_CONVERSATION_FUNNEL](_state, { conversationId, data }) {
    const funnels = _state.records[conversationId] || [];
    const index = funnels.findIndex(item => item.id === data.id);
    if (index !== -1) {
      // Preservar kanban_card_id se existir
      funnels[index] = {
        ...data,
        kanban_card_id: data.kanban_card_id
      };
    }
  },

  [types.DELETE_CONVERSATION_FUNNEL](_state, { conversationId, funnelId }) {
    const funnels = _state.records[conversationId] || [];
    const index = funnels.findIndex(item => item.id === funnelId);
    if (index !== -1) {
      funnels.splice(index, 1);
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
